import { MAP_LAYERS, MAP_LAYER_PATHS, LOGGING } from '/settings.js';
import { loadMapLayers, validateGeoJSON } from '../utils/gz-utils.js';

// In-memory cache so we only fetch+decode the map once per page load/area
let cachedMapData = null;
let cachedAreaSlug = null; // Track which area is currently cached

/**
 * Fetches and processes map data using the new layered gzipped GeoJSON approach.
 * @param {string|null} areaSlug - The slug of the area to load map data for (optional)
 * @returns {Promise<Object>} A GeoJSON FeatureCollection.
 */
export async function loadMapData(areaSlug = null) {
    // Return cached version if we've already loaded this specific area (or default map)
    if (cachedMapData && cachedAreaSlug === areaSlug) {
        if (LOGGING.ENABLED) {
            console.log(`[Map Data] Returning memoized map data for area: ${areaSlug || 'default'}`);
        }
        return cachedMapData;
    }

    try {
        if (LOGGING.ENABLED) {
            console.log(`[Map Data] Loading layered gzipped GeoJSON map for area: ${areaSlug || 'default'}...`);
        }

        // Resolve paths (handle dynamic paths for PER_AREA strategy)
        const resolvedPaths = {};
        for (const [key, pathOrFn] of Object.entries(MAP_LAYER_PATHS)) {
            resolvedPaths[key] = typeof pathOrFn === 'function' ? pathOrFn(areaSlug) : pathOrFn;
        }

        // Load all enabled layers and combine them
        const mapData = await loadMapLayers(resolvedPaths, MAP_LAYERS);

        // Validate the combined data
        if (!validateGeoJSON(mapData)) {
            throw new Error('Invalid GeoJSON data: Combined layers do not form a valid FeatureCollection');
        }

        if (LOGGING.ENABLED) {
            console.log(`[Map Data] Successfully loaded map with ${mapData.features.length} total features`);
        }

        // Memoize for subsequent callers
        cachedMapData = mapData;
        cachedAreaSlug = areaSlug;
        return mapData;
    } catch (error) {
        console.error('Error loading map data:', error);
        throw new Error(`Failed to load map data: ${error.message}`);
    }
}

/**
 * Validates that the map data contains valid features
 * @param {Object} mapData - The map data to validate
 * @throws {Error} If the data is invalid
 */
export function validateMapData(mapData) {
    if (!validateGeoJSON(mapData)) {
        throw new Error('No valid features found in the map data');
    }
} 
